import React, { FC } from 'react';
import { UUID } from '../../../../redux/models';
import { useDispatch, useSelector } from 'react-redux';
import { DroppableHeader } from '../../../../components/nestedDraggable/droppableHeader/droppableHeader';
import { useHistory } from 'react-router-dom';
import { HeaderColor } from '../../../../components/nestedDraggable/nestedDraggable.models';
import { Actions } from '../../../../redux/actions';
import { ActionButtonType } from '../../../../components/actionButtons/models';
import { AppState } from '../../../../redux/redux.model';
import { MethodologyDeep } from '../../../../redux/methodologyDeep/models';
import { DroppableHeaderV2 } from '../../../../components/nestedDraggable/droppableHeader/droppableHeaderV2';
import { useClusterModalContext } from '../../../../components/clusterModal/context/clusterModalProvider.hook';
import { useCharacteristicClustering } from '../../hooks/use-characteristic-clustering';
import methodologyClusterActions from '../../../../redux/methodologyCluster/actions';
import { getPhenomenonEditPath } from '../../../../components/phenomenonGroup/utils';

const { toggleAddCharacteristicIndicatorModal, toggleCloneCharacteristicIndicatorDrawer, deletePhenomenonFromMethodologyDeep } = Actions;

interface PhenomenonHeaderProps {
  id: UUID;
  phenomenonId: UUID;
  name: string;
  scientificName: string;
  isCanonical?: boolean;
}

const { DETAILS, EDIT } = ActionButtonType;
const ADD_BUTTON_TEXT = 'Add Characteristics/Indicators';
const CLONE_BUTTON_TEXT = 'Clone';
const CLUSTER_BUTTON_TEXT = 'Cluster';

export const PhenomenonHeader: FC<PhenomenonHeaderProps> = ({ id, phenomenonId, name, scientificName, isCanonical }) => {
  const history = useHistory();

  const methodologyId = useSelector((state: AppState) => (state.MethodologyDeep.methodologyDeep as MethodologyDeep).id);
  const companyId = useSelector((state: AppState) => (state.MethodologyDeep.methodologyDeep as MethodologyDeep).company?.id);
  const workspaceId = useSelector((state: AppState) => (state.MethodologyDeep.methodologyDeep as MethodologyDeep).workspace_id);
  const canEditCustomPhenomenon = useSelector((state: AppState) => state.App.accessFlags.canEditCustomPhenomenon);
  const flags = useSelector((state: AppState) => state.App.systemFlags);
  const { isCharacteristicClusteringEnabled } = useCharacteristicClustering();
  const { onOpenClusterModal } = useClusterModalContext();

  const onEditPhenomenon = () => {
    history.push(
      getPhenomenonEditPath({ id: phenomenonId, companyId, workspaceId }, `methodologyId=${methodologyId}&orderedPhenomenonId=${id}`)
    );
  };

  const dispatch = useDispatch();

  const onDeletePhenomenon = () => {
    dispatch(methodologyClusterActions.removeClustersByPhenomenonId(phenomenonId));
    dispatch(deletePhenomenonFromMethodologyDeep(id));
  };

  const onAddCharacteristicIndicator = () => {
    dispatch(toggleAddCharacteristicIndicatorModal({ id, phenomenonId }));
  };

  const onCloneCharacteristicIndicator = () => {
    dispatch(toggleCloneCharacteristicIndicatorDrawer({ id, phenomenonId }));
  };

  const title = `${name} (${scientificName ?? ''})`;

  return isCharacteristicClusteringEnabled ? (
    <>
      <DroppableHeaderV2 color={HeaderColor.GREEN} isCanonical={isCanonical} collapseAll>
        <DroppableHeaderV2.Options title={title}>
          <DroppableHeaderV2.OptionsDetailsOrEditButton isEdit onClick={onEditPhenomenon} />
          <DroppableHeaderV2.OptionsSeparator />
          <DroppableHeaderV2.OptionsDeleteButton onDelete={onDeletePhenomenon} />
        </DroppableHeaderV2.Options>
        <DroppableHeaderV2.ActionsSlotContainer isDraggable>
          {canEditCustomPhenomenon && flags.P40_39706_CLONE_CHARACTERISTICS_INDICATORS && (
            <DroppableHeaderV2.ActionButton label={CLONE_BUTTON_TEXT} onClick={onCloneCharacteristicIndicator} />
          )}
          <DroppableHeaderV2.ActionButton label={CLUSTER_BUTTON_TEXT} onClick={() => onOpenClusterModal([id, phenomenonId])} withIcon />
          <DroppableHeaderV2.ActionButton label={ADD_BUTTON_TEXT} onClick={onAddCharacteristicIndicator} withIcon />
        </DroppableHeaderV2.ActionsSlotContainer>
      </DroppableHeaderV2>
    </>
  ) : (
    <DroppableHeader
      id={id}
      title={<span>{title}</span>}
      color={HeaderColor.GREEN}
      collapseAll
      onEdit={onEditPhenomenon}
      onDelete={onDeletePhenomenon}
      onAdd={onAddCharacteristicIndicator}
      onClone={onCloneCharacteristicIndicator}
      addButtonText={ADD_BUTTON_TEXT}
      cloneButtonText={CLONE_BUTTON_TEXT}
      showAddButton={canEditCustomPhenomenon}
      showCloneButton={canEditCustomPhenomenon && flags.P40_39706_CLONE_CHARACTERISTICS_INDICATORS}
      dataTestId='button-add-characteristic-and-indicator'
      showCollapseAll={false}
      isCanonical={isCanonical}
      buttonDroppableType={canEditCustomPhenomenon ? EDIT : DETAILS}
    />
  );
};

export default PhenomenonHeader;
